import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { readSession } from '../lib/api.js';

type PlatformAdminRouteProps = {
  children: ReactNode;
};

export const PLATFORM_ADMIN_ROLE = 'platform_admin' as const;

/**
 * Gates platform-admin surfaces (tenant provisioning, platform-level dashboards).
 *
 * Behavior:
 * - No session → bounce to /login with `from` state (same shape as ProtectedRoute
 *   so the existing login flow resumes cleanly).
 * - Session present, wrong role → render a 404-shaped page. Critically NOT 403:
 *   the design doc (§8) requires non-platform users be told the page doesn't
 *   exist, not that they're forbidden — prevents enumeration of the wizard surface.
 */
export function PlatformAdminRoute({ children }: PlatformAdminRouteProps): JSX.Element {
  const location = useLocation();
  const { token, staff } = readSession();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (staff?.role !== PLATFORM_ADMIN_ROLE) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Not found</h1>
        <p className="ferro-muted">That page doesn't exist.</p>
      </section>
    );
  }

  return <>{children}</>;
}
